import { useState, useCallback } from 'react';
import { INGREDIENT_CARDS, DECKS, RECIPES, JUNK_RECIPE, JOKERS_DB, BOSS_BLINDS, SHOP_JOKERS } from '../data/balatroChef/gameData';

export const useBalatroChef = () => {
  // --- Game Flow States ---
  const [gamePhase, setGamePhase] = useState('deck_select'); // deck_select, playing, shop, game_over
  const [day, setDay] = useState(1);
  const [money, setMoney] = useState(0); // 👈 เงินสำหรับซื้อของในร้าน
  
  // --- Deck & Jokers ---
  const [activeDeckInfo, setActiveDeckInfo] = useState(null);
  const [masterDeck, setMasterDeck] = useState([]); // ไพ่ทั้งหมดที่มี
  const [activeJokers, setActiveJokers] = useState([]); 
  const [shopJokers, setShopJokers] = useState([]); // โจ๊กเกอร์ที่สุ่มมาขาย
  
  // --- Current Blind States ---
  const [currentBoss, setCurrentBoss] = useState(BOSS_BLINDS[0]);
  const [deck, setDeck] = useState([]); // กองจั่ว
  const [hand, setHand] = useState([]);
  const [selectedCards, setSelectedCards] = useState([]); 
  
  const [score, setScore] = useState(0); // ชิปประจำด่าน (รีเซ็ตทุกด่าน)
  const [targetScore, setTargetScore] = useState(300); 
  const [playsLeft, setPlaysLeft] = useState(4);
  const [discardsLeft, setDiscardsLeft] = useState(3);
  
  // --- UI ---
  const [lastPlayedRecipe, setLastPlayedRecipe] = useState(null); 
  const [isRecipeBookOpen, setIsRecipeBookOpen] = useState(false); 

  const initBalatroGame = useCallback(() => {
    setGamePhase('deck_select');
    setDay(1);
    setMoney(0);
    setTargetScore(300);
    setActiveJokers([]);
    setLastPlayedRecipe(null);
    setIsRecipeBookOpen(false);
  }, []);

  const drawCards = useCallback((currentHand, currentDeck) => {
    const cardsNeeded = 8 - currentHand.length;
    if (cardsNeeded <= 0 || currentDeck.length === 0) return { newHand: currentHand, newDeck: currentDeck };
    const drawn = currentDeck.slice(0, cardsNeeded);
    const remainingDeck = currentDeck.slice(cardsNeeded);
    return { newHand: [...currentHand, ...drawn], newDeck: remainingDeck };
  }, []);

  // 🌟 ฟังก์ชันเตรียมกระดานเล่นด่านใหม่
  const startBlind = useCallback((fullDeck, bossMod) => {
    // รีเซ็ตกองจั่วใหม่ทั้งหมด
    const newDrawPile = [...fullDeck].sort(() => Math.random() - 0.5);
    const { newHand, newDeck: drawnDeck } = drawCards([], newDrawPile);
    
    setHand(newHand);
    setDeck(drawnDeck);
    setSelectedCards([]);
    setScore(0); // รีเซ็ตคะแนนให้เริ่มไต่ใหม่
    
    // ตั้งค่าตามบอส
    setPlaysLeft(bossMod.id === 'boss_rush' ? 3 : 4);
    setDiscardsLeft(bossMod.id === 'boss_no_waste' ? 0 : 3);
    setGamePhase('playing');
  }, [drawCards]);

  const selectDeckAndStart = (deckId) => {
    const selectedDeck = DECKS.find(d => d.id === deckId);
    setActiveDeckInfo(selectedDeck);

    if (selectedDeck.startingJoker && JOKERS_DB[selectedDeck.startingJoker]) {
      setActiveJokers([JOKERS_DB[selectedDeck.startingJoker]]);
    }

    const fullDeck = selectedDeck.cards.map((type, index) => {
      const cardData = INGREDIENT_CARDS.find(c => c.type === type);
      return { ...cardData, uid: `card_${Date.now()}_${index}` };
    });

    setMasterDeck(fullDeck);
    setCurrentBoss(BOSS_BLINDS[0]); // ด่านแรกไม่มีบอส
    startBlind(fullDeck, BOSS_BLINDS[0]);
  };

  const toggleSelectCard = (index) => {
    setSelectedCards(prev => {
      if (prev.includes(index)) return prev.filter(i => i !== index);
      if (prev.length < 5) return [...prev, index];
      return prev;
    });
  };

  const discardSelected = () => {
    if (selectedCards.length === 0 || discardsLeft <= 0) return;
    const newHand = hand.filter((_, idx) => !selectedCards.includes(idx));
    const { newHand: drawnHand, newDeck } = drawCards(newHand, deck);
    setHand(drawnHand);
    setDeck(newDeck);
    setSelectedCards([]);
    setDiscardsLeft(prev => prev - 1);
  };

  const playSelected = () => {
    if (selectedCards.length === 0 || playsLeft <= 0) return;

    const playedCards = selectedCards.map(idx => hand[idx]);
    const playedTypes = playedCards.map(c => c.type);

    let matchedRecipe = null;
    for (const recipe of RECIPES) {
      if (recipe.required.length === playedTypes.length) {
         let tempPlayed = [...playedTypes];
         let isMatch = true;
         for (const req of recipe.required) {
            const idx = tempPlayed.indexOf(req);
            if (idx > -1) tempPlayed.splice(idx, 1);
            else { isMatch = false; break; }
         }
         if (isMatch) { matchedRecipe = recipe; break; }
      }
    }
    if (!matchedRecipe) matchedRecipe = JUNK_RECIPE;

    const totalCardChips = playedCards.reduce((sum, card) => sum + card.chips, 0);
    let finalChips = matchedRecipe.baseChips + totalCardChips;
    let finalMult = matchedRecipe.mult;
    let triggeredJokers = [];

    activeJokers.forEach(joker => {
      const buff = joker.effect(playedCards, matchedRecipe);
      if (buff) {
        if (buff.chips) finalChips += buff.chips;
        if (buff.mult) finalMult += buff.mult;
        triggeredJokers.push({ name: joker.name, icon: joker.icon, msg: buff.msg });
      }
    });

    const finalScore = finalChips * finalMult;
    setScore(prev => prev + finalScore);
    
    setLastPlayedRecipe({ 
      recipe: matchedRecipe, chips: finalChips, mult: finalMult, total: finalScore, jokers: triggeredJokers 
    });
    
    const newHand = hand.filter((_, idx) => !selectedCards.includes(idx));
    const { newHand: drawnHand, newDeck } = drawCards(newHand, deck);
    
    setHand(drawnHand);
    setDeck(newDeck);
    setSelectedCards([]);
    setPlaysLeft(prev => prev - 1);
  };

  // 🌟 ฟังก์ชันจัดการจังหวะที่กดปิด Pop-up คะแนน
  const closePopupAndCheckGameState = () => {
    const currentTotalScore = score; // ค่า score ปัจจุบันที่อัปเดตแล้ว
    setLastPlayedRecipe(null);

    // เช็กว่าผ่านด่านไหม
    if (currentTotalScore >= targetScore) {
      // ผ่านด่าน! คำนวณเงินโบนัส: ค่าผ่านด่าน (20) + (จำนวนปรุงที่เหลือ x 10) + (ทิ้งที่เหลือ x 5)
      const earnedMoney = 20 + (playsLeft * 10) + (discardsLeft * 5);
      setMoney(prev => prev + earnedMoney);
      
      // สุ่มของเข้าร้าน
      const randomJokers = [...SHOP_JOKERS].sort(() => 0.5 - Math.random()).slice(0, 2);
      setShopJokers(randomJokers);
      
      setGamePhase('shop');
    } else if (playsLeft === 0) {
      // ไม่ผ่าน
      setGamePhase('game_over');
    }
  };

  const buyJoker = (joker) => {
    if (money >= joker.price && activeJokers.length < 5) {
      setMoney(prev => prev - joker.price);
      setActiveJokers(prev => [...prev, joker]);
      // เอาออกจากร้าน
      setShopJokers(prev => prev.filter(j => j.id !== joker.id));
    }
  };

  // ไปด่านต่อไป
  const proceedToNextBlind = () => {
    const nextDay = day + 1;
    setDay(nextDay);
    setTargetScore(Math.floor(targetScore * 1.5)); // เป้าหมายยากขึ้น 1.5 เท่า
    
    const nextBoss = BOSS_BLINDS[Math.floor(Math.random() * BOSS_BLINDS.length)];
    setCurrentBoss(nextBoss);
    
    startBlind(masterDeck, nextBoss);
  };

  const toggleRecipeBook = () => setIsRecipeBookOpen(!isRecipeBookOpen); 

  return {
    gamePhase, day, money, activeDeckInfo, activeJokers, shopJokers, currentBoss,
    deck, hand, selectedCards, score, targetScore, playsLeft, discardsLeft, 
    lastPlayedRecipe, isRecipeBookOpen, 
    initBalatroGame, selectDeckAndStart, proceedToNextBlind, buyJoker,
    toggleSelectCard, discardSelected, playSelected, closePopupAndCheckGameState, toggleRecipeBook
  };
};